export type MonitoringSortColumn = 'user' | 'requests' | 'lastSeen';
export type SortDirection = 'asc' | 'desc';

export interface MonitoringSortState {
  column: MonitoringSortColumn;
  direction: SortDirection;
}

export interface MonitoringUserRow {
  name?: string;
  email: string;
  requests: number;
  minsAgo: number | null;
  desktop: number;
  mobile: number;
  tablet: number;
  active: boolean;
}

function displayName(user: MonitoringUserRow): string {
  return (user.name || user.email).toLowerCase();
}

function compareByName(a: MonitoringUserRow, b: MonitoringUserRow): number {
  return displayName(a).localeCompare(displayName(b));
}

function compareLastSeen(
  a: MonitoringUserRow,
  b: MonitoringUserRow,
  direction: SortDirection
): number {
  const aMins = a.minsAgo;
  const bMins = b.minsAgo;

  if (aMins === null && bMins === null) {
    return compareByName(a, b);
  }
  if (aMins === null) {
    return 1;
  }
  if (bMins === null) {
    return -1;
  }

  const diff = aMins - bMins;
  const primary = direction === 'asc' ? diff : -diff;
  return primary !== 0 ? primary : compareByName(a, b);
}

function compareRequests(
  a: MonitoringUserRow,
  b: MonitoringUserRow,
  direction: SortDirection
): number {
  const diff = a.requests - b.requests;
  const primary = direction === 'asc' ? diff : -diff;
  return primary !== 0 ? primary : compareByName(a, b);
}

function compareUser(
  a: MonitoringUserRow,
  b: MonitoringUserRow,
  direction: SortDirection
): number {
  const diff = compareByName(a, b);
  return direction === 'asc' ? diff : -diff;
}

/** Every column starts ascending on its first click, then toggles. */
export const DEFAULT_SORT_DIRECTION: SortDirection = 'asc';

/**
 * Returns a new sorted copy of the user list without mutating the input.
 */
export function applySort(
  users: MonitoringUserRow[],
  state: MonitoringSortState
): MonitoringUserRow[] {
  const sorted = [...users];
  sorted.sort((a, b) => {
    switch (state.column) {
      case 'lastSeen':
        return compareLastSeen(a, b, state.direction);
      case 'requests':
        return compareRequests(a, b, state.direction);
      case 'user':
        return compareUser(a, b, state.direction);
      default:
        return 0;
    }
  });
  return sorted;
}
